import React, { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { CancelToken, isCancel } from 'axios';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';

import TemplateDashboardService from '../../Services/TemplateDashboardService';

export default memo(EditDashboardModal);

const initState = {
  error: '',
  isUserPreference: false,
  requestPending: true,
};

function EditDashboardModal(props) {
  const [state, updateState] = useState(() => {
    const { isPrivate, name } = props.dashboard;
    return { ...initState, isPrivate, name };
  });
  const source = CancelToken.source();

  const checkIfUserPreference = async () => {
    try {
      await new TemplateDashboardService().checkUserPreferenceDashboard(
        props.assetID,
        props.dashboard._id,
        source.token
      );
      updateState({ ...state, isUserPreference: true, requestPending: false });
    } catch (err) {
      if (isCancel(err)) return;
      updateState({ ...state, isUserPreference: false, requestPending: false });
    }
  };

  const handleCheckboxChecked = ({ target: { id, checked } }) => {
    updateState({ ...state, [id]: checked });
  };

  const handleSave = async () => {
    try {
      updateState({ ...state, requestPending: true });
      await props.handleEditDashboardSave(state);
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState({ ...state, error, requestPending: false });
    }
  };

  useEffect(() => {
    checkIfUserPreference();
    return source.cancel;
  }, []);

  return (
    <Modal centered isOpen={props.isOpen} size="md" toggle={props.toggleModal}>
      <ModalHeader className="border-bottom-0" toggle={props.toggleModal}>
        Edit Dashboard
      </ModalHeader>
      <ModalBody className="pb-md-4">
        <div className="form-group mb-4">
          <label htmlFor="name">Dashboard Name</label>
          <input
            className="form-control form-control-sm"
            name="name"
            onChange={({ target: { value } }) =>
              updateState({ ...state, name: value })
            }
            readOnly={state.requestPending}
            type="text"
            value={state.name}
          />
        </div>
        <div className="form-check my-1">
          <input
            checked={state.isUserPreference}
            className="form-check-input"
            id="isUserPreference"
            onChange={handleCheckboxChecked}
            readOnly={state.requestPending}
            type="checkbox"
          />
          <label
            className="form-check-label text-text-light-gray"
            htmlFor="isUserPreference"
          >
            Set as your Default Dashboard?
          </label>
        </div>
        {!props.dashboard.isDefault ? (
          <div className="form-check my-1">
            <input
              checked={state.isPrivate}
              className="form-check-input"
              id="isPrivate"
              onChange={handleCheckboxChecked}
              readOnly={state.requestPending}
              type="checkbox"
            />
            <label
              className="form-check-label text-text-light-gray"
              htmlFor="isPrivate"
            >
              Set as Private Dashboard and grant other users access?
            </label>
          </div>
        ) : null}
        {state.error ? (
          <p className="text-error three-quarter-text text-right">
            {state.error}
          </p>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <button
          className="btn btn-outline-secondary text-muted bg-white shadow-none rounded-lg three-quarter-text py-0 px-3"
          disabled={state.requestPending}
          onClick={props.toggleModal}
        >
          Cancel
        </button>
        <button
          className="btn modal-button-submit rounded-lg three-quarter-text py-0 px-3"
          disabled={state.requestPending}
          onClick={handleSave}
        >
          {state.requestPending ? (
            <FontAwesomeIcon icon={faCircleNotch} spin />
          ) : (
            'Save'
          )}
        </button>
      </ModalFooter>
    </Modal>
  );
}

EditDashboardModal.propTypes = {
  assetID: PropTypes.string,
  dashboard: PropTypes.shape({
    _id: PropTypes.string,
    isDefault: PropTypes.bool,
    isPrivate: PropTypes.bool,
    name: PropTypes.string,
  }),
  handleEditDashboardSave: PropTypes.func,
  isOpen: PropTypes.bool,
  toggleModal: PropTypes.func,
};

EditDashboardModal.defaultProps = { dashboard: {} };
